/* eslint-disable no-nested-ternary */
import React, { useContext, useEffect, useState } from 'react';
import { CheckIcon } from '@heroicons/react/solid';
import { CashIcon, OfficeBuildingIcon } from '@heroicons/react/outline';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { useAggregate } from 'hooks';
import { Config } from 'configs';
import { joinClassNames } from 'utils/cssHelper';
import { getFirstDayOfMonth, getTodayDate, utcDateToMillis } from 'utils/dateManagement';
import { Link, useNavigate } from 'react-router-dom';
import { HomeCard } from './partials/homeCard';

export const HomeScreen = () => {
  const navigate = useNavigate();

  const { getCompanyRestaurants } = useContext(CompanyContext);
  const { state: auth } = useContext(AuthContext);
  const [restaurantsCount, setRestaurantsCount] = useState(0);

  const [steps, setSteps] = useState([]);
  const [wizardIsVisible, setWizardIsVisible] = useState(false);

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();

  const fromToday = new Date(year, month, day, 0, 0, 0, 0);
  const fromThisMonth = new Date(year, month, 1, 0, 0, 0, 0);
  const fromTodayInMilliseconds = utcDateToMillis(fromToday);
  const fromThisMonthInMilliseconds = utcDateToMillis(fromThisMonth);

  const requestOrdersFromData = {
    collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
    aggregateId: Config.DATA.AGGREGATES.GET_ORDERS_FROM_DATE,
    secretKey: auth.token,
    aggregateIdV2: 'AdminGetOrderTotalByDate',
    companyIdForV2: auth.company?.company,
  };

  const [, todayTotalOrders] = useAggregate({
    ...requestOrdersFromData,
    filter: { companyId: auth.company?.company, dateFrom: fromTodayInMilliseconds },
  });
  const [, monthTotalOrders] = useAggregate({
    ...requestOrdersFromData,
    filter: { companyId: auth.company?.company, dateFrom: fromThisMonthInMilliseconds },
  });

  useEffect(() => {
    const fetchCounts = async () => {
      const restaurants = await getCompanyRestaurants({
        token: auth.token,
        companyId: auth.company?.company,
      });

      const restaurantStepIsCompleted = restaurants && restaurants.length > 0;

      const isWizardVisible = restaurantStepIsCompleted; // && employeesStepIsCompleted && productsStepIsCompleted;

      const stepItems = [
        {
          id: '01',
          name: 'Create restaurants',
          description: 'Add restaurants to your company',
          href: '/settings/restaurants',
          status: restaurants && restaurants.length > 0 ? 'complete' : 'current',
        },
      ];

      setRestaurantsCount(restaurants ? restaurants.length : 0);

      setSteps(stepItems);
      setWizardIsVisible(!isWizardVisible);
    };

    fetchCounts();
  }, []);

  return (
    <>
      {wizardIsVisible && (
        <div className="lg:border-t lg:border-b lg:border-gray-200">
          <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" aria-label="Progress">
            <ol className="rounded-md overflow-hidden lg:flex lg:border-l lg:border-r lg:border-gray-200 lg:rounded-none">
              {steps.map((step, stepIdx) => (
                <li key={step.id} className="relative overflow-hidden lg:flex-1">
                  <div
                    className={joinClassNames(
                      stepIdx === 0 ? 'border-b-0 rounded-t-md' : '',
                      stepIdx === steps.length - 1 ? 'border-t-0 rounded-b-md' : '',
                      'border border-gray-200 overflow-hidden lg:border-0',
                    )}
                  >
                    {step.status === 'complete' ? (
                      <Link to={step.href} className="group">
                        <span
                          className="absolute top-0 left-0 w-1 h-full bg-transparent group-hover:bg-gray-200 lg:w-full lg:h-1 lg:bottom-0 lg:top-auto"
                          aria-hidden="true"
                        />
                        <span
                          className={joinClassNames(
                            stepIdx !== 0 ? 'lg:pl-9' : '',
                            'px-6 py-5 flex items-start text-sm font-medium',
                          )}
                        >
                          <span className="flex-shrink-0">
                            <span className="w-10 h-10 flex items-center justify-center bg-indigo-600 rounded-full">
                              <CheckIcon className="w-6 h-6 text-white" aria-hidden="true" />
                            </span>
                          </span>
                          <span className="mt-0.5 ml-4 min-w-0 flex flex-col">
                            <span className="text-xs font-semibold tracking-wide uppercase">
                              {step.name}
                            </span>
                            <span className="text-sm font-medium text-gray-500">
                              {step.description}
                            </span>
                          </span>
                        </span>
                      </Link>
                    ) : step.status === 'current' ? (
                      <Link to={step.href} aria-current="step">
                        <span
                          className="absolute top-0 left-0 w-1 h-full bg-indigo-600 lg:w-full lg:h-1 lg:bottom-0 lg:top-auto"
                          aria-hidden="true"
                        />
                        <span
                          className={joinClassNames(
                            stepIdx !== 0 ? 'lg:pl-9' : '',
                            'px-6 py-5 flex items-start text-sm font-medium',
                          )}
                        >
                          <span className="flex-shrink-0">
                            <span className="w-10 h-10 flex items-center justify-center border-2 border-indigo-600 rounded-full">
                              <span className="text-indigo-600">{step.id}</span>
                            </span>
                          </span>
                          <span className="mt-0.5 ml-4 min-w-0 flex flex-col">
                            <span className="text-xs font-semibold text-indigo-600 tracking-wide uppercase">
                              {step.name}
                            </span>
                            <span className="text-sm font-medium text-gray-500">
                              {step.description}
                            </span>
                          </span>
                        </span>
                      </Link>
                    ) : (
                      <Link to={step.href} className="group">
                        <span
                          className="absolute top-0 left-0 w-1 h-full bg-transparent group-hover:bg-gray-200 lg:w-full lg:h-1 lg:bottom-0 lg:top-auto"
                          aria-hidden="true"
                        />
                        <span
                          className={joinClassNames(
                            stepIdx !== 0 ? 'lg:pl-9' : '',
                            'px-6 py-5 flex items-start text-sm font-medium',
                          )}
                        >
                          <span className="flex-shrink-0">
                            <span className="w-10 h-10 flex items-center justify-center border-2 border-gray-300 rounded-full">
                              <span className="text-gray-500">{step.id}</span>
                            </span>
                          </span>
                          <span className="mt-0.5 ml-4 min-w-0 flex flex-col">
                            <span className="text-xs font-semibold text-gray-500 tracking-wide uppercase">
                              {step.name}
                            </span>
                            <span className="text-sm font-medium text-gray-500">
                              {step.description}
                            </span>
                          </span>
                        </span>
                      </Link>
                    )}

                    {stepIdx !== 0 ? (
                      <>
                        {/* Separator */}
                        <div
                          className="hidden absolute top-0 left-0 w-3 inset-0 lg:block"
                          aria-hidden="true"
                        >
                          <svg
                            className="h-full w-full text-gray-300"
                            viewBox="0 0 12 82"
                            fill="none"
                            preserveAspectRatio="none"
                          >
                            <path
                              d="M0.5 0V31L10.5 41L0.5 51V82"
                              stroke="currentcolor"
                              vectorEffect="non-scaling-stroke"
                            />
                          </svg>
                        </div>
                      </>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          </nav>
        </div>
      )}

      <div className="mt-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-lg leading-6 font-medium text-gray-900">Overview</h2>
          <div className="mt-2 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <HomeCard
              title="Restaurants"
              count={restaurantsCount}
              link="/settings/restaurants"
              icon={<OfficeBuildingIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />}
            />

            <HomeCard
              title="Orders today"
              count={
                todayTotalOrders && todayTotalOrders.length > 0 ? todayTotalOrders[0].amount : 0
              }
              onClick={() => {
                localStorage.setItem('orders-filter-from', JSON.stringify(getTodayDate()));
                localStorage.setItem('orders-filter-to', null);
                navigate('/orders');
              }}
              icon={<CashIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />}
            />

            <HomeCard
              title="Orders this month"
              count={
                monthTotalOrders && monthTotalOrders.length > 0 ? monthTotalOrders[0].amount : 0
              }
              onClick={() => {
                localStorage.setItem('orders-filter-from', JSON.stringify(getFirstDayOfMonth()));
                localStorage.setItem('orders-filter-to', null);
                navigate('/orders');
              }}
              icon={<CashIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />}
            />
          </div>
        </div>
      </div>
    </>
  );
};
